import { type FC } from 'react';
import type { LegacyConversationCardInteractionProps } from 'src/modules/communications/types/legacy-conversation-card.type';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { LegacyInteractionIndicators } from './interaction-indicators';

/** Renders the legacy card's direction, status, and message preview. */
export const LegacyConversationCardInteraction: FC<
  LegacyConversationCardInteractionProps
> = ({ interaction, previewLines = 1, className = '' }) => {
  const showDesktopTooltip = useMediaQuery('(min-width: 640px)');
  const previewHeightClassName = previewLines === 2 ? 'h-8' : 'h-4';

  if (!interaction) {
    return (
      <div className={`${previewHeightClassName} ${className}`}>
        <Text size="tiny" color="secondary" className="text-secondary-500">
          Sin interacciones
        </Text>
      </div>
    );
  }

  const hasLongText =
    typeof interaction.snippet === 'string' && interaction.snippet.length > 80;
  const previewText = interaction.snippet ?? 'Sin interacciones recientes';

  const containerClasses = `relative flex items-start transition-all duration-200 ${previewHeightClassName} ${className}`;

  const snippetClasses = `
    text-xs text-secondary-600 leading-[1.25]
    break-all break-words overflow-wrap-anywhere ${previewLines === 2 ? 'line-clamp-2' : 'line-clamp-1'}
  `;

  return (
    <div className={containerClasses}>
      <div className="flex h-full w-full items-start gap-1.5">
        <div className="flex shrink-0 items-center gap-1 pt-0.5">
          <LegacyInteractionIndicators interaction={interaction} />
        </div>

        <div className={`min-w-0 flex-1 ${previewHeightClassName}`}>
          {hasLongText && showDesktopTooltip ? (
            <Tooltip
              content={`${previewText.slice(0, 150)}${
                previewText.length > 150 ? '…' : ''
              }`}
              size="sm"
              placement="top"
              className="max-w-[24rem]"
              showDelay={1}
            >
              <div className={snippetClasses}>{previewText}</div>
            </Tooltip>
          ) : (
            <Text size="tiny" color="secondary" className={snippetClasses}>
              {previewText}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
